import * as TabsPrimitive from '@radix-ui/react-tabs';
import { motion, AnimatePresence } from 'framer-motion';
import {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  createContext,
  useContext,
  forwardRef,
  Children,
  cloneElement,
  isValidElement,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useProximityHover } from '@/Domain/hooks/Shared/use-proximity-hover';
import { fontWeights } from '@/Domain/utils/font-weight';
import type { IconComponent } from '@/Domain/utils/icon-map';
import { useShape } from '@/Domain/utils/shape-context';
import { springs } from '@/Domain/utils/springs';
import { cn } from '@/Domain/utils/utils';

/* ─────────────────────── Contexts ─────────────────────── */

interface TabsValueOrderContextValue {
  valueOrder: string[];
  setValueOrder: (order: string[]) => void;
  selectedValue: string | undefined;
}

const TabsValueOrderContext = createContext<TabsValueOrderContextValue | null>(null);

interface TabsListContextValue {
  registerTab: (index: number, value: string, el: HTMLElement | null, disabled?: boolean) => void;
  hoveredIndex: number | null;
  selectedValue: string | undefined;
  /** Optimistically set selectedIdx so the indicator moves immediately on click. */
  setOptimisticIdx: (index: number) => void;
}

const TabsListContext = createContext<TabsListContextValue | null>(null);

function useTabsList() {
  const ctx = useContext(TabsListContext);
  if (!ctx) throw new Error('TabItem must be used within a TabsList');
  return ctx;
}

/* ─────────────────────── Tabs (Root) ─────────────────────── */

interface TabsProps extends Omit<
  ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
  'onValueChange' | 'onSelect'
> {
  /** Controlled value (takes precedence over selectedIndex). */
  value?: string;
  /** Called when the active tab changes. */
  onValueChange?: (value: string) => void;
  /** Index-based controlled alternative. */
  selectedIndex?: number;
  /** Called with the new index when the active tab changes. */
  onSelect?: (index: number) => void;
}

const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ value, onValueChange, selectedIndex, onSelect, defaultValue, children, ...props }, ref) => {
    const [valueOrder, setValueOrder] = useState<string[]>([]);
    const [uncontrolledValue, setUncontrolledValue] = useState<string | undefined>(defaultValue);
    const updateValueOrder = useCallback((order: string[]) => {
      setValueOrder((current) => {
        if (
          current.length === order.length &&
          current.every((value, index) => value === order[index])
        ) {
          return current;
        }
        return order;
      });
    }, []);

    // Resolve value: explicit value > selectedIndex lookup > defaultValue
    const resolvedValue =
      value ?? (selectedIndex != null ? valueOrder[selectedIndex] : uncontrolledValue);

    const handleValueChange = useCallback(
      (newValue: string) => {
        if (value === undefined && selectedIndex == null) {
          setUncontrolledValue(newValue);
        }
        onValueChange?.(newValue);
        if (onSelect) {
          const idx = valueOrder.indexOf(newValue);
          if (idx !== -1) onSelect(idx);
        }
      },
      [onValueChange, onSelect, valueOrder],
    );

    return (
      <TabsValueOrderContext.Provider
        value={{
          valueOrder,
          setValueOrder: updateValueOrder,
          selectedValue: resolvedValue,
        }}
      >
        <TabsPrimitive.Root
          ref={ref}
          value={resolvedValue}
          onValueChange={handleValueChange}
          defaultValue={resolvedValue == null ? defaultValue : undefined}
          activationMode="automatic"
          {...props}
        >
          {children}
        </TabsPrimitive.Root>
      </TabsValueOrderContext.Provider>
    );
  },
);

Tabs.displayName = 'Tabs';

/* ─────────────────────── TabsList ─────────────────────── */

type TabsListProps = ComponentPropsWithoutRef<typeof TabsPrimitive.List>;

const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ children, className, ...props }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const isMouseInside = useRef(false);
    const shape = useShape();
    const valueOrderCtx = useContext(TabsValueOrderContext);
    const [optimisticIdx, setOptimisticIdx] = useState<number | null>(null);

    // Derive value order from children synchronously
    const values = Children.toArray(children)
      .filter(isValidElement)
      .map((child) => (child.props as { value?: string }).value)
      .filter((v): v is string => typeof v === 'string');
    const valueOrderKey = values.join(',');
    const setValueOrder = valueOrderCtx?.setValueOrder;

    // Report value order up to Tabs root
    useLayoutEffect(() => {
      setValueOrder?.(values);
    }, [setValueOrder, valueOrderKey]);

    // Proximity hover
    const {
      activeIndex: hoveredIndex,
      setActiveIndex: setHoveredIndex,
      itemRects,
      handlers,
      registerItem,
      measureItems,
    } = useProximityHover(containerRef, { axis: 'x' });

    // Register items: bridge from (index, value, el, disabled) → registerItem.
    // Disabled tabs are registered (so the proximity logic knows the cursor
    // is over them and can suppress hover for the whole list) but flagged so
    // they are excluded from the closest-tab fallback.
    const registerTab = useCallback(
      (index: number, _value: string, el: HTMLElement | null, disabled = false) => {
        registerItem(index, el, disabled);
      },
      [registerItem],
    );

    // Measure on children change
    useEffect(() => {
      measureItems();
    }, [measureItems, children]);

    // Remeasure on resize
    useEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => measureItems());
      ro.observe(el);
      return () => ro.disconnect();
    }, [measureItems]);

    // Track mouse inside
    const handleMouseMove = useCallback(
      (e: React.MouseEvent) => {
        isMouseInside.current = true;
        handlers.onMouseMove(e);
      },
      [handlers],
    );

    const handleMouseLeave = useCallback(() => {
      isMouseInside.current = false;
      handlers.onMouseLeave();
    }, [handlers]);

    // Focus ring
    const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
    const selectedValue = valueOrderCtx?.selectedValue;
    const selectedIdx = selectedValue !== undefined ? values.indexOf(selectedValue) : -1;

    useEffect(() => {
      setOptimisticIdx(selectedIdx >= 0 ? selectedIdx : null);
    }, [selectedIdx]);

    const activeSelectedIdx = optimisticIdx;
    const selectedRect = activeSelectedIdx !== null ? itemRects[activeSelectedIdx] : null;
    const hoverRect = hoveredIndex !== null ? itemRects[hoveredIndex] : null;
    const focusRect = focusedIndex !== null ? itemRects[focusedIndex] : null;
    const isHoveringSelected = hoveredIndex === activeSelectedIdx;
    const isHovering = hoveredIndex !== null && !isHoveringSelected;

    // Auto-assign _index to children
    const indexedChildren = Children.map(children, (child, i) => {
      if (isValidElement(child)) {
        return cloneElement(child, { _index: i } as Record<string, unknown>);
      }
      return child;
    });

    return (
      <TabsListContext.Provider
        value={{
          registerTab,
          hoveredIndex,
          selectedValue,
          setOptimisticIdx,
        }}
      >
        <TabsPrimitive.List
          ref={(node) => {
            (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onFocus={(e) => {
            const trigger = (e.target as HTMLElement).closest('[role="tab"]');
            if (!trigger) return;
            const indexAttr = trigger.getAttribute('data-proximity-index');
            if (indexAttr != null) {
              const idx = Number(indexAttr);
              setHoveredIndex(idx);
              setFocusedIndex((e.target as HTMLElement).matches(':focus-visible') ? idx : null);
            }
          }}
          onBlur={(e) => {
            if (containerRef.current?.contains(e.relatedTarget as Node)) return;
            setFocusedIndex(null);
            if (isMouseInside.current) return;
            setHoveredIndex(null);
          }}
          className={cn(
            'relative inline-flex items-center gap-0.5 p-1 select-none bg-muted max-sm:max-w-full max-sm:overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            shape.container,
            className,
          )}
          {...props}
        >
          {/* Active segment indicator */}
          {selectedRect && (
            <motion.div
              className={cn(
                'absolute pointer-events-none bg-background shadow-sm dark:shadow-[0_1px_2px_rgba(0,0,0,0.4)]',
                shape.bg,
              )}
              initial={false}
              animate={{
                left: selectedRect.left,
                width: selectedRect.width,
                top: selectedRect.top,
                height: selectedRect.height,
                opacity: isHovering ? 0.85 : 1,
              }}
              transition={{
                ...springs.moderate,
                opacity: { duration: 0.08 },
              }}
            />
          )}

          {/* Hover indicator */}
          <AnimatePresence>
            {hoverRect && !isHoveringSelected && selectedRect && (
              <motion.div
                className={cn(
                  'absolute pointer-events-none bg-accent/40 dark:bg-accent/25',
                  shape.bg,
                )}
                initial={{
                  left: selectedRect.left,
                  width: selectedRect.width,
                  top: selectedRect.top,
                  height: selectedRect.height,
                  opacity: 0,
                }}
                animate={{
                  left: hoverRect.left,
                  width: hoverRect.width,
                  top: hoverRect.top,
                  height: hoverRect.height,
                  opacity: 0.4,
                }}
                exit={
                  !isMouseInside.current && selectedRect
                    ? {
                        left: selectedRect.left,
                        width: selectedRect.width,
                        top: selectedRect.top,
                        height: selectedRect.height,
                        opacity: 0,
                        transition: {
                          ...springs.moderate,
                          opacity: { duration: 0.06 },
                        },
                      }
                    : { opacity: 0, transition: { duration: 0.06 } }
                }
                transition={{
                  ...springs.fast,
                  opacity: { duration: 0.08 },
                }}
              />
            )}
          </AnimatePresence>

          {/* Focus ring */}
          <AnimatePresence>
            {focusRect && (
              <motion.div
                className={cn(
                  'absolute pointer-events-none z-20 border border-[#6B97FF]',
                  shape.focusRing,
                )}
                initial={false}
                animate={{
                  left: focusRect.left - 2,
                  top: focusRect.top - 2,
                  width: focusRect.width + 4,
                  height: focusRect.height + 4,
                }}
                exit={{ opacity: 0, transition: { duration: 0.06 } }}
                transition={{
                  ...springs.fast,
                  opacity: { duration: 0.08 },
                }}
              />
            )}
          </AnimatePresence>

          {indexedChildren}
        </TabsPrimitive.List>
      </TabsListContext.Provider>
    );
  },
);

TabsList.displayName = 'TabsList';

/* ─────────────────────── TabItem ─────────────────────── */

interface TabItemProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  /** Unique value for this tab. */
  value: string;
  /** Optional leading icon. */
  icon?: IconComponent;
  /** Text label (preferred). Falls back to children for backward compat. */
  label?: string;
  /** Optional hover tooltip. Wraps the trigger in Radix Tooltip; requires a TooltipProvider in the tree. */
  tooltip?: ReactNode;
  /** @internal Auto-assigned by TabsList. */
  _index?: number;
}

const TabItem = forwardRef<HTMLButtonElement, TabItemProps>(
  ({ value, icon: Icon, label, tooltip, children, _index = 0, className, ...props }, ref) => {
    const internalRef = useRef<HTMLButtonElement>(null);
    const { registerTab, hoveredIndex, selectedValue, setOptimisticIdx } = useTabsList();
    // Disabled tabs are registered with the proximity-hover system but
    // flagged as disabled. The hook then (a) excludes them from the
    // "closest enabled neighbor" fallback so a disabled tab can't
    // bleed hover into a sibling, and (b) clears `hoveredIndex` outright
    // when the cursor sits inside a disabled tab's rect. The tooltip
    // wrapper still receives pointer events because the trigger itself
    // doesn't disable pointer-events at the CSS level.
    const isDisabled = props.disabled === true;

    useEffect(() => {
      registerTab(_index, value, internalRef.current, isDisabled);
      return () => registerTab(_index, value, null);
    }, [_index, value, registerTab, isDisabled]);

    const isSelected = selectedValue === value;
    const isActive = !isDisabled && (hoveredIndex === _index || isSelected);

    // Extract text for the font-weight animation trick.
    // If label is provided, use it. Otherwise render children directly.
    const displayLabel = label ?? (typeof children === 'string' ? children : null);

    const trigger = (
      <TabsPrimitive.Trigger
        onClick={() => {
          if (isDisabled) return;
          setOptimisticIdx(_index);
        }}
        ref={(node) => {
          (internalRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
        }}
        value={value}
        data-proximity-index={_index}
        className={cn(
          'relative z-10 flex items-center justify-center gap-2 px-3 py-1 bg-transparent border-none outline-none',
          isDisabled ? 'cursor-not-allowed text-foreground-subtle opacity-60' : 'cursor-pointer',
          className,
        )}
        {...props}
      >
        {Icon && (
          <Icon
            size={16}
            strokeWidth={1.5}
            className={cn(
              'transition-colors duration-80',
              isActive ? 'text-foreground' : 'text-muted-foreground',
            )}
          />
        )}
        {displayLabel ? (
          <span className="inline-grid text-sm whitespace-nowrap">
            <span
              className="col-start-1 row-start-1 invisible"
              style={{ fontVariationSettings: fontWeights.semibold }}
              aria-hidden="true"
            >
              {displayLabel}
            </span>
            <span
              className={cn(
                'col-start-1 row-start-1 transition-[color,font-variation-settings] duration-80',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}
              style={{
                fontVariationSettings: isSelected ? fontWeights.semibold : fontWeights.normal,
              }}
            >
              {displayLabel}
            </span>
          </span>
        ) : children ? (
          <span className="inline-grid text-sm whitespace-nowrap">
            <span
              className="invisible col-start-1 row-start-1 flex items-center gap-1.5"
              style={{ fontVariationSettings: fontWeights.semibold }}
              aria-hidden="true"
            >
              {children}
            </span>
            <span
              className={cn(
                'col-start-1 row-start-1 flex items-center gap-1.5 transition-[color,font-variation-settings] duration-80',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}
              style={{
                fontVariationSettings: isSelected ? fontWeights.semibold : fontWeights.normal,
              }}
            >
              {children}
            </span>
          </span>
        ) : null}
      </TabsPrimitive.Trigger>
    );

    // Always render the Tooltip wrapper so the React tree stays the same shape when
    // the `tooltip` prop toggles. Wrap in our own TooltipProvider so this works
    // regardless of whether the parent provided one.
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          {tooltip != null && <TooltipContent side="bottom">{tooltip}</TooltipContent>}
        </Tooltip>
      </TooltipProvider>
    );
  },
);

TabItem.displayName = 'TabItem';

/* ─────────────────────── TabPanel ─────────────────────── */

interface TabPanelProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.Content> {
  /** Must match a TabItem value. */
  value: string;
}

const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn('outline-none', className)} {...props} />
));

TabPanel.displayName = 'TabPanel';

/* ─────────────────────── Exports ─────────────────────── */

/* Backward-compatible aliases for migration */
const TabsContent = TabPanel;
const TabsTrigger = TabItem;

export { Tabs, TabsList, TabItem, TabPanel, TabsContent, TabsTrigger };
export type { TabsProps, TabsListProps, TabItemProps, TabPanelProps };
